/**
 * Slot registry — the human-facing map of every promoted position on the
 * storefront, mirroring `promoted-highlights-mapping.md`.
 *
 * This is what powers the visual "pick a page → click a slot" admin flow.
 * Non-technical users never see an etid; they see "the big left card on the
 * Landing page". The etid/scope/feed metadata lives here so the UI can quietly
 * translate a clicked slot into the correct backend call.
 */

export type SurfaceKey = 'main' | 'market' | 'homes' | 'menu';
export type SectionKey = 'hero' | 'mid' | 'bottom' | 'promo';
export type SectionLayout = 'hero' | 'mosaic' | 'bottom' | 'menu';

/** Which backend feed fills a slot by default. */
export type FeedKind = 'promotions' | 'highlights';

/** The four content pillars a highlights slot can default to. */
export interface Pillar {
  etid: number;
  name: string;
  color: string; // tailwind text/bg accent seed
}

export const PILLARS: Record<number, Pillar> = {
  21: { etid: 21, name: 'Locals', color: 'emerald' },
  32: { etid: 32, name: 'Market', color: 'blue' },
  2: { etid: 2, name: 'Homes', color: 'violet' },
  7: { etid: 7, name: 'Vehicles', color: 'amber' },
};

export const pillarName = (etid?: number): string =>
  (etid != null && PILLARS[etid]?.name) || 'Category';

export interface SlotDef {
  /** Stable slot key, e.g. `main.hero.1`. Never shown unless "advanced" is on. */
  key: string;
  section: SectionKey;
  /** Friendly name shown on the tile, e.g. "Big left card". */
  name: string;
  /** Plain-language position, e.g. "mosaic big left". */
  position: string;
  feed: FeedKind;
  /** Highlights slots only: the category (etid) + scope that fill the card. */
  etid?: number;
  scope?: 'self' | 'children';
  /** Which card in the pillar's fan-out (0-based) this slot shows. */
  card?: number;
  /** How many cards the slot rotates (hero = 1, highlights = up to 3). */
  slides: number;
  /** Menu tiles depend on the hovered pillar, so their source is dynamic. */
  dynamic?: boolean;
  /**
   * Responsive grid-placement classes for a mosaic/menu section. Tiles flow
   * naturally on mobile and take their bespoke position from `sm:` up. Kept as
   * static strings so Tailwind's content scan generates them.
   */
  cellClass?: string;
}

export interface SectionDef {
  key: SectionKey;
  label: string;
  hint: string;
  layout: SectionLayout;
  slots: SlotDef[];
}

export interface SurfaceDef {
  key: SurfaceKey;
  name: string;
  tagline: string;
  /** Emoji used as a lightweight page icon in the picker. */
  glyph: string;
  sections: SectionDef[];
}

const hero = (surface: string, n: number): SlotDef => ({
  key: `${surface}.hero.${n}`,
  section: 'hero',
  name: `Hero slide ${n}`,
  position: `hero slider ${n}`,
  feed: 'promotions',
  slides: 1,
});

/** Landing / Market / Homes all share the same 5-tile blended mosaic. */
const blendedMosaic = (surface: string): SlotDef[] => [
  {
    key: `${surface}.mid.1`,
    section: 'mid',
    name: 'Big left card',
    position: 'mosaic big left',
    feed: 'highlights',
    etid: 21,
    scope: 'children',
    card: 0,
    slides: 3,
    cellClass: 'col-span-2 sm:col-span-1 sm:col-start-1 sm:row-start-1 sm:row-end-3',
  },
  {
    key: `${surface}.mid.2`,
    section: 'mid',
    name: 'Center top',
    position: 'mosaic center-top',
    feed: 'highlights',
    etid: 32,
    scope: 'children',
    card: 0,
    slides: 3,
    cellClass: 'col-span-2 sm:col-start-2 sm:col-end-4 sm:row-start-1',
  },
  {
    key: `${surface}.mid.3`,
    section: 'mid',
    name: 'Center bottom-left',
    position: 'mosaic center bottom-left',
    feed: 'highlights',
    etid: 2,
    scope: 'children',
    card: 0,
    slides: 3,
    cellClass: 'sm:col-start-2 sm:row-start-2',
  },
  {
    key: `${surface}.mid.4`,
    section: 'mid',
    name: 'Center bottom-right',
    position: 'mosaic center bottom-right',
    feed: 'highlights',
    etid: 7,
    scope: 'self',
    card: 0,
    slides: 3,
    cellClass: 'sm:col-start-3 sm:row-start-2',
  },
  {
    key: `${surface}.mid.5`,
    section: 'mid',
    name: 'Right rail',
    position: 'mosaic right rail',
    feed: 'highlights',
    etid: 32,
    scope: 'children',
    card: 1,
    slides: 3,
    cellClass: 'col-span-2 sm:col-span-1 sm:col-start-4 sm:row-start-1 sm:row-end-3',
  },
];

export const SURFACES: SurfaceDef[] = [
  {
    key: 'main',
    name: 'Landing page',
    tagline: 'The first thing everyone sees',
    glyph: '🏠',
    sections: [
      {
        key: 'hero',
        label: 'Hero banners',
        hint: 'The big rotating banners at the very top.',
        layout: 'hero',
        slots: [1, 2, 3, 4].map((n) => hero('main', n)),
      },
      {
        key: 'mid',
        label: 'Featured mosaic',
        hint: 'The mixed grid of category cards in the middle.',
        layout: 'mosaic',
        slots: blendedMosaic('main'),
      },
      {
        key: 'bottom',
        label: 'Bottom strip',
        hint: 'Two slim cards near the foot of the page.',
        layout: 'bottom',
        slots: [
          {
            key: 'main.bottom.1',
            section: 'bottom',
            name: 'Slim left',
            position: 'bottom slim left',
            feed: 'highlights',
            etid: 32,
            scope: 'children',
            card: 0,
            slides: 3,
          },
          {
            key: 'main.bottom.2',
            section: 'bottom',
            name: 'Slim right',
            position: 'bottom slim right',
            feed: 'highlights',
            etid: 2,
            scope: 'children',
            card: 0,
            slides: 3,
          },
        ],
      },
    ],
  },
  {
    key: 'market',
    name: 'Market page',
    tagline: 'The marketplace landing surface',
    glyph: '🛍️',
    sections: [
      {
        key: 'hero',
        label: 'Hero banners',
        hint: 'The big rotating banners at the very top.',
        layout: 'hero',
        slots: [1, 2, 3, 4].map((n) => hero('market', n)),
      },
      {
        key: 'mid',
        label: 'Featured mosaic',
        hint: 'The mixed grid of category cards in the middle.',
        layout: 'mosaic',
        slots: blendedMosaic('market'),
      },
      {
        key: 'bottom',
        label: 'Bottom strip',
        hint: 'Two slim market cards near the foot of the page.',
        layout: 'bottom',
        slots: [
          {
            key: 'market.bottom.1',
            section: 'bottom',
            name: 'Slim left',
            position: 'bottom slim left',
            feed: 'highlights',
            etid: 32,
            scope: 'children',
            card: 0,
            slides: 3,
          },
          {
            key: 'market.bottom.2',
            section: 'bottom',
            name: 'Slim right',
            position: 'bottom slim right',
            feed: 'highlights',
            etid: 32,
            scope: 'children',
            card: 1,
            slides: 3,
          },
        ],
      },
    ],
  },
  {
    key: 'homes',
    name: 'Homes page',
    tagline: 'The homes & property surface',
    glyph: '🏡',
    sections: [
      {
        key: 'hero',
        label: 'Hero banners',
        hint: 'The big rotating banners at the very top.',
        layout: 'hero',
        slots: [1, 2, 3, 4].map((n) => hero('homes', n)),
      },
      {
        key: 'mid',
        label: 'Featured mosaic',
        hint: 'The mixed grid of category cards in the middle.',
        layout: 'mosaic',
        slots: blendedMosaic('homes'),
      },
    ],
  },
  {
    key: 'menu',
    name: 'Mega menu',
    tagline: 'The 3 promo tiles in the nav dropdown',
    glyph: '📂',
    sections: [
      {
        key: 'promo',
        label: 'Menu tiles',
        hint: 'The three promo tiles on the right of the mega menu. What they show follows the category the shopper is hovering.',
        layout: 'menu',
        slots: [
          {
            key: 'menu.promo.1',
            section: 'promo',
            name: 'Featured tile',
            position: 'tall featured tile',
            feed: 'highlights',
            card: 0,
            slides: 3,
            dynamic: true,
            cellClass: 'col-span-2 sm:col-span-1 sm:col-start-1 sm:row-start-1 sm:row-end-3',
          },
          {
            key: 'menu.promo.2',
            section: 'promo',
            name: 'Top tile',
            position: 'top stacked tile',
            feed: 'highlights',
            card: 1,
            slides: 3,
            dynamic: true,
            cellClass: 'sm:col-start-2 sm:row-start-1',
          },
          {
            key: 'menu.promo.3',
            section: 'promo',
            name: 'Bottom tile',
            position: 'bottom stacked tile',
            feed: 'highlights',
            card: 2,
            slides: 3,
            dynamic: true,
            cellClass: 'sm:col-start-2 sm:row-start-2',
          },
        ],
      },
    ],
  },
];

export const getSurface = (key: SurfaceKey): SurfaceDef =>
  SURFACES.find((s) => s.key === key)!;

/** Human sentence describing what fills a slot by default. */
export const describeDefaultSource = (slot: SlotDef): string => {
  if (slot.feed === 'promotions') {
    return 'Filled by the Promotions system — whichever paid banners are live for this page.';
  }
  if (slot.dynamic) {
    return 'Shows cards from whichever category the shopper is browsing in the menu.';
  }
  const pillar = pillarName(slot.etid);
  const what =
    slot.scope === 'children' ? `${pillar} sub-categories` : pillar;
  return `Shows ${what} automatically.`;
};
